import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import {
  DatePickerField,
  TimePickerField,
  formatDateLabel,
  formatTimeLabel,
  getEarliestBookableDate,
  toISODate,
} from "../../StrategyCanvas/DateTimePicker/DateTimePicker";
// Reuses Canvas's own CompleteReviewModal.scss (.crm-* classes) rather than
// writing new CSS - same "Send to CEO" modal design, just re-worded for
// items/blueprint instead of blocks/canvas. Field names match the submit
// body exactly (COACH-API.md §1 #8): note_to_ceo / scheduled_date /
// scheduled_time / duration_minutes.
import "../../StrategyCanvas/StrategyCanvasReview/CompleteReviewModal/CompleteReviewModal.scss";
// ⚠️ THE SECOND IMPORT IS GONE (2026-09-09). This used to also pull in
// `./ExecutionBlueprintReview.scss`, purely for the duration field's rules,
// which lived there while the field was EXW-only. Canvas's modal now has the
// same field, so those rules moved into the stylesheet imported above and were
// renamed `ebr-duration-*` → `crm-duration-*`. Every class this file renders is
// a `crm-*` one, so the screen-shell stylesheet had nothing left to give it.

// ── Duration ────────────────────────────────────────────────────────────────
// ⚠️ AN OPEN FIELD, NOT PILLS (2026-09-03, on instruction). This was
// `DURATION_OPTIONS = [30, 45, 60, 90]` rendered as four toggle buttons, so a
// 40- or 75-minute session could not be booked at all.
//
// The bounds are the validation, and they are deliberately wide: this is the
// coach typing how long they expect to need, not a slot-booking system, so the
// field should refuse only what cannot be a real session length.
//   5   — below this is a typo, not a meeting.
//   480 — eight hours. Past this the coach has mistyped, most likely by adding
//         a digit (450 → 4500).
const DURATION_MIN = 5;
const DURATION_MAX = 480;
const DEFAULT_DURATION = "45";

// ⚠️ THERE IS NO RESTING HINT UNDER THIS FIELD, by two rounds of instruction.
// It first read "Between 5 and 480 minutes." — the validation range printed at
// the coach, which named an eight-hour session as though it were an option.
// Reworded to "Most live reviews take 45–60 minutes.", then removed outright:
// the field is one labelled box holding a number and the word "min", and it
// does not need explaining. The bounds still exist and only speak up when one
// is broken — see `durationError`.

// Digits only. Applied on every change rather than validated on submit, so the
// field simply cannot hold anything else — see `handleDurationChange` for the
// several ways a number input lets non-numbers through.
const DIGITS_ONLY = /[^0-9]/g;

const NOTE_LIMIT = 500;

const DATE_PRESETS = [
  // "Today" is bookable (the shared picker's hint says "from today"), and it is
  // the most likely date of all — same preset as Strategy Canvas.
  { key: "today", label: "Today", days: 0 },
  { key: "tomorrow", label: "Tomorrow", days: 1 },
  { key: "in3", label: "In 3 days", days: 3 },
  { key: "nextWeek", label: "Next week", days: 7 },
];
const PRESET_DEFAULT_TIME = "14:00";

const addDays = (days) => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + days);
  return toISODate(date);
};

// "Execution Blueprint Review Complete" modal - shown when the coach clicks
// "Complete review & send to CEO" (only enabled once every item has a
// review decision). Same structure/interaction as Strategy Canvas's own
// CompleteReviewModal, copy adjusted for items/blueprint.
// ⚠️ SHARED WITH ANNUAL OKR (2026-09-21). `title` and `itemNoun` were added so
// the Annual OKR coach review can reuse this modal rather than grow a near-copy
// of 470 lines — including the date presets, the picker fields and the duration
// field's hard-won `type="text"` handling. Both default to the Execution
// Blueprint's wording, so every existing call site is unchanged.
//
// The two contracts' submit bodies are identical (`note_to_ceo`,
// `scheduled_date`, `scheduled_time`, `duration_minutes`), which is what makes
// the sharing honest rather than a coincidence worth exploiting — EXW handoff
// §4 and Annual OKR §6.7 describe the same payload.
const CompleteReviewModal = ({
  totalItems,
  acceptedCount,
  toDiscussCount,
  commentCount,
  submitting,
  onCancel,
  onSubmit,
  title = "Review complete",
  itemNoun = "WIG",
}) => {
  const [scheduling, setScheduling] = useState(false);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [duration, setDuration] = useState(DEFAULT_DURATION);
  const [activePreset, setActivePreset] = useState(null);
  const [note, setNote] = useState("");

  const earliest = getEarliestBookableDate();

  const hasDate = !!date;
  const hasTime = !!time;

  // ── Duration validity ────────────────────────────────────────────────────
  // The pills could not be wrong — one was always selected. An open field can
  // be empty or out of range, so the send has to account for it or the modal
  // would POST `duration_minutes: NaN`.
  const durationNumber = duration === "" ? null : Number(duration);
  const durationOutOfRange =
    durationNumber !== null &&
    (durationNumber < DURATION_MIN || durationNumber > DURATION_MAX);
  // Only a problem while scheduling is on AND a session is actually being
  // booked — an empty duration on a modal being sent without a session is
  // irrelevant, and blocking it would be a dead end with no visible cause.
  const durationInvalid =
    scheduling &&
    hasDate &&
    hasTime &&
    (durationNumber === null || durationOutOfRange);

  // The one thing that is wrong, worded for the bound that was actually broken
  // rather than reciting both. `null` when the field is fine, so it doubles as
  // the "is there anything to say?" flag for the hint slot below.
  //
  // The maximum is expressed in HOURS. "480 minutes" makes the reader do the
  // arithmetic to find out whether they are near it; "8 hours" makes it
  // obvious at a glance that they have mistyped — which is the only way to
  // reach this message.
  const durationError = (() => {
    if (!durationInvalid) return null;
    if (durationNumber === null)
      return "Enter how long the session should run.";
    if (durationNumber < DURATION_MIN) {
      return `Give the session at least ${DURATION_MIN} minutes.`;
    }
    return `${DURATION_MAX / 60} hours is the longest session you can book.`;
  })();

  const scheduleIncomplete =
    (scheduling && hasDate !== hasTime) || durationInvalid;

  const handlePreset = (preset) => {
    setActivePreset(preset.key);
    setDate(addDays(preset.days));
    setTime((prev) => prev || PRESET_DEFAULT_TIME);
  };

  const handleDateChange = (value) => {
    setDate(value);
    setActivePreset(null);
  };

  // ⚠️ STRIPPED, NOT VALIDATED. `<input type="number">` is not enough on its
  // own and that is the whole reason this handler exists: it accepts `e`,
  // `E`, `+`, `-` and `.` as legitimate keystrokes (they can begin a valid
  // number like `1e3` or `-4`), and for those `event.target.value` comes back
  // as the EMPTY STRING rather than the text the user typed — so a validity
  // check sees "empty" and cannot tell a cleared field from `12e`.
  //
  // So the input is `type="text"` with `inputMode="numeric"` — which still
  // raises the numeric keypad on mobile — and every change is filtered down to
  // digits here. Paste, drag-and-drop and autofill all route through this too,
  // which a `keydown` blocklist would have missed.
  //
  // Leading zeros are stripped so "045" cannot be submitted, but an empty
  // field stays empty rather than snapping to "0": clearing it to retype is a
  // normal thing to do, and a field that fills itself with 0 mid-edit fights
  // the person typing.
  const handleDurationChange = (event) => {
    const digits = event.target.value.replace(DIGITS_ONLY, "");
    setDuration(digits === "" ? "" : String(Number(digits)));
  };

  // Empty on blur returns to the default rather than staying blank. The field
  // is only reachable while a session is being scheduled, and a session with
  // no length is not a state worth preserving — whereas leaving it blank would
  // block the send with the error sitting in a field the coach has left.
  const handleDurationBlur = () => {
    if (duration === "") setDuration(DEFAULT_DURATION);
  };

  const handleToggleScheduling = () => {
    setScheduling((prev) => {
      if (prev) {
        setDate("");
        setTime("");
        setActivePreset(null);
      }
      return !prev;
    });
  };

  const handleSend = () => {
    if (scheduleIncomplete) return;
    const body = {};
    if (note.trim()) body.note_to_ceo = note.trim();
    if (scheduling && hasDate && hasTime) {
      body.scheduled_date = date;
      body.scheduled_time = time;
      // A number, not the field's string. `durationInvalid` has already
      // blocked the send if this could be null or out of range.
      body.duration_minutes = durationNumber;
    }
    onSubmit(body);
  };

  const summaryLine = () => {
    // The field already says exactly what is wrong, right next to itself, so
    // the footer only has to say that something is — repeating the sentence
    // here made the same words appear twice on screen at once.
    if (durationInvalid) {
      return "Check the session length before sending.";
    }
    if (scheduleIncomplete) {
      return "Finish the session date and time, or switch scheduling off to send without one.";
    }
    const parts = [
      `${toDiscussCount} ${itemNoun}${toDiscussCount === 1 ? "" : "s"} to discuss and ${commentCount} comment${
        commentCount === 1 ? "" : "s"
      }`,
    ];
    if (note.trim()) parts.push("your note");
    if (scheduling && hasDate && hasTime) {
      parts.push(
        `a ${durationNumber} min session on ${formatDateLabel(date)} at ${formatTimeLabel(time)}`,
      );
    }
    const list =
      parts.length > 1
        ? `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`
        : parts[0];
    return `The CEO will receive ${list}.`;
  };

  return (
    <Modal
      show
      onHide={onCancel}
      centered
      dialogClassName="crm-dialog"
      contentClassName="crm-content"
      size="lg"
    >
      <Modal.Body className="crm-body">
        <button
          type="button"
          className="crm-close"
          onClick={onCancel}
          aria-label="Close"
        >
          <i className="fa-solid fa-xmark" aria-hidden="true" />
        </button>

        <div className="crm-header">
          <span className="crm-header-icon">
            <i className="fa-solid fa-circle-check" aria-hidden="true" />
          </span>
          <div>
            <h3 className="crm-title">{title}</h3>
            {/* Pluralised, like every other count in this modal — the footer
                recap has always said "1 item to discuss" correctly, and this
                line was the one place that could render "All 1 items". */}
            <p className="crm-subtitle">
              All {totalItems} {itemNoun}
              {totalItems === 1 ? "" : "s"} reviewed. Here&apos;s what the CEO
              will receive.
            </p>
          </div>
        </div>

        <div className="crm-scroll">
          <div className="crm-stats">
            <div className="crm-stat crm-stat--accepted">
              <span className="crm-stat-value">{acceptedCount}</span>
              <span className="crm-stat-label">Accepted</span>
            </div>
            <div className="crm-stat crm-stat--discuss">
              <span className="crm-stat-value">{toDiscussCount}</span>
              <span className="crm-stat-label">To discuss</span>
            </div>
            <div className="crm-stat crm-stat--comments">
              <span className="crm-stat-value">{commentCount}</span>
              <span className="crm-stat-label">Comments</span>
            </div>
          </div>

          <div className="crm-section">
            <div className="crm-section-head">
              <i
                className="fa-regular fa-calendar-check crm-section-icon"
                aria-hidden="true"
              />
              <span className="crm-section-title">
                Schedule the live review
                <span className="crm-optional">Optional</span>
              </span>
              <button
                type="button"
                className={`crm-switch${scheduling ? " crm-switch--on" : ""}`}
                onClick={handleToggleScheduling}
                role="switch"
                aria-checked={scheduling}
                aria-label="Schedule the live review"
              />
            </div>

            {scheduling && (
              <div className="crm-section-body">
                <div className="crm-presets">
                  {DATE_PRESETS.map((preset) => (
                    <button
                      type="button"
                      key={preset.key}
                      className={`crm-preset${
                        activePreset === preset.key ? " crm-preset--on" : ""
                      }`}
                      onClick={() => handlePreset(preset)}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>

                <div className="crm-field-row">
                  <DatePickerField
                    value={date}
                    onChange={handleDateChange}
                    minDate={earliest}
                    error={
                      !hasDate && hasTime
                        ? "Pick a date to schedule a session."
                        : ""
                    }
                  />
                  <TimePickerField
                    value={time}
                    onChange={setTime}
                    error={
                      hasDate && !hasTime
                        ? "Pick a time too — both are needed."
                        : ""
                    }
                  />
                </div>

                {/* ⚠️ AN OPEN FIELD, replacing four 30/45/60/90 pills
                    (2026-09-03, on instruction) — a 40- or 75-minute session
                    could not be booked before.

                    `type="text"` with `inputMode="numeric"`, NOT
                    `type="number"`. See `handleDurationChange` for why: a
                    number input reports an empty value for partial entries
                    like `12e`, which makes "cleared" and "invalid" impossible
                    to tell apart. It also brings spinners nobody wants on a
                    minutes box and a scroll-wheel that silently changes the
                    value while the coach is scrolling the modal. */}
                <div className="crm-duration">
                  {/* The unit lives HERE, not inside the field (2026-09-03, on
                      instruction). A "min" suffix sitting in the box was the
                      only thing in this row that wasn't a value, and it forced
                      the number into a narrow slot beside it. In the label it
                      is read once, before the field, the way a form label is
                      supposed to work — and the box is then free to hold just
                      the number, like Date and Time hold just their values. */}
                  <label className="crm-duration-label" htmlFor="crm-duration">
                    Duration (min)
                  </label>
                  <div
                    className={`crm-duration-field${
                      durationInvalid ? " crm-duration-field--error" : ""
                    }`}
                  >
                    {/* The leading icon Date and Time both carry — theirs are
                        `fa-regular fa-calendar` and `fa-regular fa-clock`, so
                        this is the regular-weight hourglass rather than a
                        solid stopwatch, which would sit heavier than its two
                        neighbours in the same column. */}
                    <i
                      className="crm-duration-icon fa-regular fa-hourglass"
                      aria-hidden="true"
                    />
                    <input
                      id="crm-duration"
                      className="crm-duration-input"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      value={duration}
                      onChange={handleDurationChange}
                      onBlur={handleDurationBlur}
                      // 3 digits is DURATION_MAX's width — a 4th could only
                      // ever be out of range, so the field stops accepting
                      // input rather than accepting it and then complaining.
                      maxLength={3}
                      aria-describedby={
                        durationError ? "crm-duration-hint" : undefined
                      }
                      aria-invalid={durationInvalid || undefined}
                    />
                  </div>
                  {/* Only ever the error. Nothing renders at rest — see the
                      note on the removed hint constant above. */}
                  {durationError && (
                    <span
                      className="crm-duration-hint crm-duration-hint--error"
                      id="crm-duration-hint"
                    >
                      {durationError}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="crm-section">
            <div className="crm-section-head">
              <i
                className="fa-regular fa-message crm-section-icon"
                aria-hidden="true"
              />
              <span className="crm-section-title">
                Note to the CEO
                <span className="crm-optional">Optional</span>
              </span>
            </div>
            <div className="crm-section-body">
              <textarea
                rows={4}
                maxLength={NOTE_LIMIT}
                placeholder="Anything to read before the session — what to focus on, what stood out…"
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
              <div className="crm-note-count">
                {note.length}/{NOTE_LIMIT}
              </div>
            </div>
          </div>
        </div>

        <div className="crm-footer">
          <p
            className={`crm-footer-recap${scheduleIncomplete ? " crm-footer-recap--warn" : ""}`}
          >
            <i
              className={
                scheduleIncomplete
                  ? "fa-solid fa-triangle-exclamation"
                  : "fa-solid fa-circle-info"
              }
              aria-hidden="true"
            />
            <span>{summaryLine()}</span>
          </p>

          <div className="crm-footer-actions">
            <button
              type="button"
              className="crm-cancel-btn"
              onClick={onCancel}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="crm-send-btn"
              onClick={handleSend}
              disabled={submitting || scheduleIncomplete}
            >
              {submitting ? "Sending…" : "Send to CEO"}
              <i className="fa-solid fa-arrow-right" aria-hidden="true" />
            </button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default CompleteReviewModal;
